import React, { useEffect, useState, useMemo } from "react";
import { InputField } from "@cakrawalajakarta/shared-components";
// import { Country, State, City } from "country-state-city"; // Dependency removed to save size
import { AnimatePresence, motion } from "framer-motion";
import { useNotification } from "../common/NotificationContext";
import { db } from "@lib/firebase";
import { doc, setDoc, getDoc } from "firebase/firestore";
import { serverTimestamp } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { storage } from "@lib/firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

export const InputFieldRace = ({
  formData,
  handleChange,
  isFormDisabled = false,
  labelColor,
  eventId,
  categoryName,
  updateUserData,
  isForeigner,
}) => {
  const { showNotification } = useNotification();
  const [countries, setCountries] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [rawCountries, setRawCountries] = useState([]);
  const [provinceIds, setProvinceIds] = useState({});
  const [countryMap, setCountryMap] = useState({});
  const [stateMap, setStateMap] = useState({});
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState({
    countries: false,
    provinces: false,
    cities: false,
  });
  const [participantProvinces, setParticipantProvinces] = useState([
    [],
    [],
    [],
  ]);
  const [participantCities, setParticipantCities] = useState([[], [], []]);
  const [participantProvinceIds, setParticipantProvinceIds] = useState([
    {},
    {},
    {},
  ]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentParticipantIndex, setCurrentParticipantIndex] = useState(0);
  const [genderError, setGenderError] = useState("");

  const [isDraftLoaded, setIsDraftLoaded] = useState(false);
  const [uploadingCertificate, setUploadingCertificate] = useState(false);

  const GEO_API_BASE = `${import.meta.env.VITE_API_BASE_URL}/api/geo`;
  const sizeChartUrl = "/jersey/sizechart.jpg";

  const cache = useMemo(() => new Map(), []);
  const isGroupRegistration =
    (eventId === "JOTR" || eventId === "JOTR2026") && categoryName?.includes("Group");
  const normalizedCategoryName = String(categoryName || "").toUpperCase();
  const isFunRun5KCategory =
    eventId === "FUNRUN5K2026" || normalizedCategoryName.includes("FUN RUN 5K CIBODAS");
  const skipsRaceProof = eventId === "ROADTOJOTR2026" || isFunRun5KCategory;

  const humanize = (str) => {
    if (!str) return "";
    if (str === "nomorIdentitas") return "ID Number";
    if (str === "teamName") return "Team Name";
    if (str === "namaDepan") return "First Name";
    if (str === "namaBelakang") return "Last Name";
    if (str === "bibName") return "BIB Name";
    if (str === "telepon") return "Phone Number";
    if (str === "tanggalLahir") return "Date of Birth";
    if (str === "golonganDarah") return "Blood Type";
    if (str === "sizeJersey") return "Jersey Size";
    if (str === "gender") return "Gender";
    if (str === "negara") return "Country";
    if (str === "provinsi") return "Province";
    if (str === "kota") return "City";
    if (str === "alamat") return "Address";
    if (str === "kontakDarurat") return "Emergency Contact";
    if (str === "namaKontakDarurat") return "Emergency Contact Name";
    if (str === "hubunganKontakDarurat") return "Emergency Contact Relationship";
    if (str === "performanceIndex") return "Performance Index";
    if (str === "raceResultLink") return "Race Result Link";
    
    const withSpaces = str.replace(/([A-Z])/g, " $1").trim();
    return withSpaces
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  // Fetch data from Firestore on component mount (only once)
  useEffect(() => {
    if (!isGroupRegistration || isDraftLoaded) return;

    const fetchRegistrationData = async () => {
      const auth = getAuth();
      const userId = auth.currentUser?.uid;
      if (!userId || !eventId) {
        console.error(
          "[DEBUG] Missing userId or eventId for fetching registration data"
        );
        showNotification(
          "error",
          "Failed to load registration data from server.",
          "Close"
        );
        return;
      }

      try {
        const docRef = doc(db, "registrations", `${userId}_${eventId}`);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          console.log(
            "[DEBUG] Fetched registration data from Firestore:",
            data
          );

          const defaultParticipant = {
            namaDepan: "",
            namaBelakang: "",
            bibName: "",
            telepon: "",
            nomorIdentitas: "",
            tanggalLahir: "",
            golonganDarah: "",
            sizeJersey: "",
            gender: "",
            negara: "Indonesia",
            provinsi: "",
            kota: "",
            alamat: "",
            kontakDarurat: "",
            namaKontakDarurat: "",
            hubunganKontakDarurat: "",
            performanceIndex: "",
            raceResultLink: "",
          };

          const participants = Array(3)
            .fill()
            .map((_, index) => ({
              ...defaultParticipant,
              ...(data.participants && data.participants[index]
                ? data.participants[index]
                : {}),
            }));

          handleChange({
            target: {
              name: "participants",
              value: participants,
            },
          });

          if (data.teamName) {
            handleChange({
              target: {
                name: "teamName",
                value: data.teamName,
              },
            });
          }

          console.log("[DEBUG] Applied registration data to formData:", {
            participants,
            teamName: data.teamName,
          });
        } else {
          console.log(
            "[DEBUG] No registration data found in Firestore, using default formData."
          );
        }
      } catch (err) {
        console.error("[ERROR] Error fetching registration data:", err);
        showNotification("error", "Failed to load registration data from server.", "Close");
      } finally {
        setIsDraftLoaded(true);
      }
    };

    fetchRegistrationData();
  }, [
    eventId,
    isGroupRegistration,
    handleChange,
    showNotification,
    isDraftLoaded,
  ]);

  const fetchWithCache = async (
    url,
    cacheKey,
    type,
    participantIndex = null
  ) => {
    if (cache.has(cacheKey)) {
      return cache.get(cacheKey);
    }
    setLoading((prev) => ({ ...prev, [type]: true }));
    try {
      const res = await fetch(url);
      const data = await res.json();
      if (data?.geonames) {
        cache.set(cacheKey, data);
        return data;
      } else {
        setErrors((prev) => ({
          ...prev,
          [participantIndex !== null ? `${type}_${participantIndex}` : type]:
            "Failed to load location data. Please try again later.",
        }));
        showNotification(
          "error",
          "Failed to load location data. Please try again later.",
          "Close"
        );
        return null;
      }
    } catch (err) {
      console.error(`[ERROR] Error fetching ${cacheKey}:`, err);
      setErrors((prev) => ({
        ...prev,
        [participantIndex !== null ? `${type}_${participantIndex}` : type]:
          "Failed to load location data. Please try again later.",
      }));
      showNotification(
        "error",
        "Failed to load location data. Please try again later.",
        "Close"
      );
      return null;
    } finally {
      setLoading((prev) => ({ ...prev, [type]: false }));
    }
  };

  useEffect(() => {
    // Only use GeoNames API
    fetchWithCache(
      `${GEO_API_BASE}/countries`,
      "countries",
      "countries"
    ).then((data) => {
      if (data?.geonames) {
        const countryList = data.geonames.map((country) => ({
          value: country.countryName,
          label: country.countryName,
        })).sort((a, b) => a.label.localeCompare(b.label));
        setCountries(countryList);
        setRawCountries(data.geonames);
      } else {
        setCountries([]);
        setRawCountries([]);
      }
    });
  }, [cache]);

  useEffect(() => {
    if (formData.negara && !isGroupRegistration) {
      setProvinces([]);
      setCities([]);

      // Use GeoNames API
      if (rawCountries.length > 0) {
        const selectedCountry = rawCountries.find(
          (c) => c.countryName === formData.negara
        );
        if (!selectedCountry) return;
  
        fetchWithCache(
          `${GEO_API_BASE}/children/${selectedCountry.geonameId}?lang=en`,
          `provinces_${selectedCountry.geonameId}`,
          "provinces"
        ).then((data) => {
          if (data?.geonames) {
            const provinceList = data.geonames.map((prov) => ({
              value: prov.name,
              label: prov.name,
            })).sort((a, b) => a.label.localeCompare(b.label));
            const idMap = {};
            data.geonames.forEach((prov) => {
              idMap[prov.name] = prov.geonameId;
            });
            setProvinces(provinceList);
            setProvinceIds(idMap);
          }
        });
      }
    }
  }, [formData.negara, rawCountries, isGroupRegistration]);

  useEffect(() => {
    if (
      formData.provinsi &&
      !isGroupRegistration
    ) {
      setCities([]);

      // Use GeoNames API
      if (provinceIds[formData.provinsi]) {
        const provinceId = provinceIds[formData.provinsi];
  
        fetchWithCache(
          `${GEO_API_BASE}/children/${provinceId}?lang=en`,
          `cities_${provinceId}`,
          "cities"
        ).then((data) => {
          if (data?.geonames) {
            const cityList = data.geonames.map((city) => ({
              value: city.name,
              label: city.name,
            })).sort((a, b) => a.label.localeCompare(b.label));
            setCities(cityList);
          }
        });
      }
    }
  }, [formData.provinsi, formData.negara, provinceIds, isGroupRegistration]);

  useEffect(() => {
    if (isGroupRegistration) {
      const participant = formData.participants[currentParticipantIndex];
      if (participant?.negara) {
        
        // Use GeoNames API
        if (rawCountries.length > 0) {
            const selectedCountry = rawCountries.find(
            (c) => c.countryName === participant.negara
            );
            if (!selectedCountry) return;
    
            fetchWithCache(
            `${GEO_API_BASE}/children/${selectedCountry.geonameId}?lang=en`,
            `provinces_${selectedCountry.geonameId}_${currentParticipantIndex}`,
            "provinces",
            currentParticipantIndex
            ).then((data) => {
            if (data?.geonames) {
                const provinceList = data.geonames.map((prov) => ({
                value: prov.name,
                label: prov.name,
                })).sort((a, b) => a.label.localeCompare(b.label));
                const idMap = {};
                data.geonames.forEach((prov) => {
                idMap[prov.name] = prov.geonameId;
                });
                setParticipantProvinces((prev) => {
                const newProvinces = [...prev];
                newProvinces[currentParticipantIndex] = provinceList;
                return newProvinces;
                });
                setParticipantProvinceIds((prev) => {
                const newIds = [...prev];
                newIds[currentParticipantIndex] = idMap;
                return newIds;
                });
    
                if (participant.provinsi && idMap[participant.provinsi]) {
                fetchWithCache(
                    `${GEO_API_BASE}/children/${
                    idMap[participant.provinsi]
                    }?lang=en`,
                    `cities_${
                    idMap[participant.provinsi]
                    }_${currentParticipantIndex}`,
                    "cities",
                    currentParticipantIndex
                ).then((data) => {
                    if (data?.geonames) {
                    const cityList = data.geonames.map((city) => ({
                        value: city.name,
                        label: city.name,
                    })).sort((a, b) => a.label.localeCompare(b.label));
                    setParticipantCities((prev) => {
                        const newCities = [...prev];
                        newCities[currentParticipantIndex] = cityList;
                        return newCities;
                    });
                    }
                });
                }
            }
            });
        }
      }
    }
  }, [
    formData.participants,
    rawCountries,
    isGroupRegistration,
    currentParticipantIndex,
    cache
  ]);

  const validateField = (
    name,
    value,
    participantIndex = null,
    negara = "Indonesia"
  ) => {
    const label = humanize(name);

    // Validate required fields - but make provinsi and kota optional if no options available
    if (value === undefined || value === null || value === "") {
      // For provinsi, check if there are provinces available for the selected country
      if (name === "provinsi") {
        const provinceList = participantIndex !== null ? participantProvinces[participantIndex] : provinces;
        if (provinceList.length === 0) {
          return ""; // No error if no provinces available
        }
      }
      // For kota, check if there are cities available for the selected province
      if (name === "kota") {
        const cityList = participantIndex !== null ? participantCities[participantIndex] : cities;
        if (cityList.length === 0) {
          return ""; // No error if no cities available
        }
      }
      return `${label} is required`;
    }

    if (name === "teamName" && value) {
      if (!/^[A-Za-z0-9\s]{3,50}$/.test(value)) {
        return `${label} must be 3-50 characters, alphanumeric with spaces, e.g., "Fast Running Team"`;
      }
    }
    if (name === "namaDepan" && value) {
      if (!/^[A-Za-z\s]{1,50}$/.test(value)) {
        return `${label} must only contain letters and spaces, max 50 characters, e.g., "John"`;
      }
    }
    if (name === "namaBelakang" && value) {
      if (!/^[A-Za-z\s]{1,50}$/.test(value)) {
        return `${label} must only contain letters and spaces, max 50 characters, e.g., "Doe"`;
      }
    }
    if (name === "bibName" && value) {
      if (!/^[A-Za-z0-9\s]{1,20}$/.test(value)) {
        return `${label} must be 1-20 characters alphanumeric and spaces, e.g., "JOHN123"`;
      }
    }
    if (name === "telepon" && value) {
      if (!/^\+?\d{8,15}$/.test(value)) {
        return `${label} must be 8-15 digits, can start with "+", for example: "+6281234567890"`;
      }
    }
    if (name === "nomorIdentitas" && value) {
      if (negara === "Indonesia") {
        if (!/^\d{16}$/.test(value)) {
          return `${label} (KTP) must be 16 digits for Indonesian participants, e.g., "1234567890123456"`;
        }
      } else {
        if (!/^[A-Za-z0-9]{5,20}$/.test(value)) {
          return `${label} (Passport/ID) must be 5-20 alphanumeric characters for foreign participants, e.g., "AB123456"`;
        }
      }
    }
    if (name === "tanggalLahir" && value) {
      const date = new Date(value);
      const eventDate = new Date("2026-07-24");
      const minAgeDate = new Date(
        eventDate.getFullYear() - 17,
        eventDate.getMonth(),
        eventDate.getDate()
      );
      if (isNaN(date.getTime()) || date >= eventDate) {
        return `${label} must be in the past before 24/07/2026`;
      }
      if (date > minAgeDate) {
        return `Participants must be at least 17 years old on 24/07/2026`;
      }
    }
    if (name === "golonganDarah" && value) {
      if (!["A", "B", "AB", "O"].includes(value)) {
        return `${label} must be A, B, AB, or O`;
      }
    }
    if (name === "sizeJersey" && value) {
      if (!["XS", "S", "M", "L", "XL", "XXL"].includes(value)) {
        return `${label} must be XS, S, M, L, XL, or XXL`;
      }
    }
    if (name === "gender" && value) {
      if (!["Male", "Female", "Laki-laki", "Perempuan", "Pria", "Wanita"].includes(value)) {
        return `${label} must be Male or Female`;
      }
    }
    if (name === "negara" && value) {
      if (countries.length > 0 && !countries.some((c) => c.value === value)) {
        return `${label} must be selected from the available list, e.g., "Indonesia"`;
      }
    }
    if (name === "provinsi" && value) {
      const provinceList =
        participantIndex !== null
          ? participantProvinces[participantIndex]
          : provinces;
      if (
        provinceList.length > 0 &&
        !provinceList.some((p) => p.value === value)
      ) {
        return `${label} must be selected from the available list, e.g., "West Java"`;
      }
    }
    if (name === "kota" && value) {
      const cityList =
        participantIndex !== null
          ? participantCities[participantIndex]
          : cities;
      if (
        cityList.length > 0 &&
        !cityList.some((c) => c.value === value)
      ) {
        return `${label} must be selected from the available list, e.g., "Jakarta"`;
      }
    }
    if (name === "alamat" && value && value.trim().length < 5) {
      return `${label} is too short`;
    }
    if (name === "kontakDarurat" && value) {
      if (!/^\+?\d{8,15}$/.test(value)) {
        return `${label} must be 8-15 digits, can start with "+", for example: "+6281234567890"`;
      }
    }
    if (name === "namaKontakDarurat" && value) {
      if (!/^[A-Za-z\s]{1,50}$/.test(value)) {
        return `${label} must only contain letters and spaces, max 50 characters, e.g., "Ann"`;
      }
    }
    if (name === "hubunganKontakDarurat" && value) {
      if (!/^[A-Za-z\s]{1,50}$/.test(value)) {
        return `${label} must only contain letters and spaces, max 50 characters, e.g., "Friend"`;
      }
    }
    if (name === "performanceIndex" && value) {
      if (!/^\d{3}$/.test(value)) {
        return `${label} must be 3 numeric digits, e.g., "460"`;
      }
    }
    if (name === "raceResultLink" && value) {
      return "";
    }
    if (name === "riwayatMedis") {
       // Optional field for Medical History
       return "";
    }
    if (name === "certificateUrl") {
      // Optional field
      return "";
    }
    return "";
  };

  const validateIndividual = () => {
    const requiredFields = [
      "namaDepan",
      "namaBelakang",
      "bibName",
      "telepon",
      "nomorIdentitas",
      "tanggalLahir",
      "golonganDarah",
      "sizeJersey",
      "gender",
      "negara",
      "provinsi",
      "kota",
      "alamat",
      "kontakDarurat",
      "namaKontakDarurat",
      "hubunganKontakDarurat",
      ...(skipsRaceProof ? [] : ["raceResultLink"]),
    ];
    const errors = {};
    let isValid = true;

    requiredFields.forEach((field) => {
      const error = validateField(
        field,
        formData[field],
        null,
        formData.negara
      );
      if (error && touched[field]) {
        errors[field] = error;
        isValid = false;
      }
    });

    return { isValid, errors };
  };

  const validateParticipant = (participantData, index) => {
    const requiredFields = [
      "namaDepan",
      "namaBelakang",
      "bibName",
      "telepon",
      "nomorIdentitas",
      "tanggalLahir",
      "golonganDarah",
      "sizeJersey",
      "gender",
      "negara",
      "provinsi",
      "kota",
      "alamat",
      "kontakDarurat",
      "namaKontakDarurat",
      "hubunganKontakDarurat",
      ...(skipsRaceProof ? [] : ["raceResultLink"]),
    ];
    const errors = {};
    let isValid = true;

    requiredFields.forEach((field) => {
      const error = validateField(
        field,
        participantData[field],
        index,
        participantData.negara
      );
      if (error) {
        errors[`${field}_${index}`] = error;
        isValid = false;
      }
    });

    return { isValid, errors };
  };

  const validateAllParticipants = async () => {
    let allValid = true;
    let allErrors = {};
    const bibNames = new Set();

    setTouched((prev) => {
      const newTouched = { ...prev };
      formData.participants.forEach((participant, index) => {
        Object.keys(participant).forEach((field) => {
          newTouched[`${field}_${index}`] = true;
        });
      });
      return newTouched;
    });

    for (let index = 0; index < formData.participants.length; index++) {
      const participant = formData.participants[index];
      const { isValid, errors } = validateParticipant(participant, index);
      if (!isValid) {
        allValid = false;
        allErrors = { ...allErrors, ...errors };
      }
      if (participant.bibName) {
        if (bibNames.has(participant.bibName)) {
          allErrors[`bibName_${index}`] = "BIB Name already in use";
          allValid = false;
        } else {
          bibNames.add(participant.bibName);
        }
      }
    }

    const allMale = formData.participants.every(
      (p) => p.gender === "Male" || p.gender === "Laki-laki"
    );
    if (allMale) {
      setGenderError(
        "Team cannot consist of all men. At least one participant must be female."
      );
      allValid = false;
    } else {
      setGenderError("");
    }

    setErrors(allErrors);
    return allValid;
  };

  const handleFieldChange = (e, participantIndex = null) => {
    const { name, value } = e.target;
    handleChange(e, participantIndex);
    setTouched((prev) => ({
      ...prev,
      [participantIndex !== null ? `${name}_${participantIndex}` : name]: true,
    }));
    const error = validateField(
      name,
      value,
      participantIndex,
      participantIndex !== null
        ? formData.participants[participantIndex]?.negara
        : formData.negara
    );
    setErrors((prev) => ({
      ...prev,
      [participantIndex !== null ? `${name}_${participantIndex}` : name]: error,
    }));
  };

  const saveToFirestore = async (showSuccessNotification = false) => {
    const auth = getAuth();
    const userId = auth.currentUser?.uid;
    if (!userId || !eventId) {
      console.error("[ERROR] Missing userId or eventId for Firestore save");
      showNotification(
        "error",
        "Failed to save data: User or event information is incomplete.",
        "Close"
      );
      return false;
    }

    try {
      const docRef = doc(db, "registrations", `${userId}_${eventId}`);
      const draftData = {
        participants: formData.participants,
        teamName: formData.teamName || "",
        eventId,
        userId,
        status: "draft",
        updatedAt: serverTimestamp(),
      };
      await setDoc(docRef, draftData);
      if (showSuccessNotification) {
        showNotification(
          "success",
          "All participants' data has been temporarily saved!",
          "Close"
        );
      }
      return true;
    } catch (err) {
      console.error("[ERROR] Error saving to Firestore:", err);
      showNotification("error", "Failed to save participant data to server.", "Close");
      return false;
    }
  };

  const handleNextParticipant = async () => {
    const currentParticipant = formData.participants[currentParticipantIndex];
    if (!currentParticipant) {
      console.error(
        "[ERROR] Current participant is undefined at index:",
        currentParticipantIndex
      );
      showNotification(
        "error",
        `Data for participant ${currentParticipantIndex + 1} not found.`,
        "Close"
      );
      return;
    }
    const { isValid, errors: newErrors } = validateParticipant(
      currentParticipant,
      currentParticipantIndex
    );

    setTouched((prev) => {
      const newTouched = { ...prev };
      Object.keys(currentParticipant).forEach((field) => {
        newTouched[`${field}_${currentParticipantIndex}`] = true;
      });
      return newTouched;
    });

    setErrors((prev) => ({ ...prev, ...newErrors }));

    if (!isValid) {
      const firstErrorKey = Object.keys(newErrors).find(
        (key) => newErrors[key]
      );
      const firstError = firstErrorKey
        ? newErrors[firstErrorKey]
        : "Please complete participant data correctly.";
      showNotification("error", firstError, "Close");
      return;
    }

    const success = await saveToFirestore();
    if (!success) return;

    if (currentParticipantIndex === 0 && updateUserData) {
      const updatedFields = {
        namaDepan: currentParticipant.namaDepan,
        namaBelakang: currentParticipant.namaBelakang,
        namaLengkap:
          `${currentParticipant.namaDepan} ${currentParticipant.namaBelakang}`.trim(),
        telepon: currentParticipant.telepon,
        nomorIdentitas: currentParticipant.nomorIdentitas,
        tanggalLahir: currentParticipant.tanggalLahir,
        gender: currentParticipant.gender,
        negara: currentParticipant.negara,
        provinsi: currentParticipant.provinsi,
        kota: currentParticipant.kota,
        alamat: currentParticipant.alamat,
        kontakDarurat: currentParticipant.kontakDarurat,
        namaKontakDarurat: currentParticipant.namaKontakDarurat,
        hubunganKontakDarurat: currentParticipant.hubunganKontakDarurat,
        bibName: currentParticipant.bibName,
        golonganDarah: currentParticipant.golonganDarah,
        sizeJersey: currentParticipant.sizeJersey,
        performanceIndex: currentParticipant.performanceIndex,
        raceResultLink: currentParticipant.raceResultLink,
        certificateUrl: currentParticipant.certificateUrl,
      };

      try {
        await updateUserData(updatedFields);
      } catch (err) {
        console.error("[ERROR] Error updating user data:", err);
        showNotification("error", "Category or distance data is incomplete.", "Close");
      }
    }

    if (currentParticipantIndex < 2) {
      setCurrentParticipantIndex(currentParticipantIndex + 1);
      setGenderError("");
    } else {
      const allValid = await validateAllParticipants();
      if (allValid) {
        await saveToFirestore(true);
        showNotification(
          "success",
          "All participants' data has been temporarily saved! Please proceed to submit.",
          "Close"
        );
      } else {
        const firstErrorKey = Object.keys(errors).find((key) => errors[key]);
        const firstError = firstErrorKey
          ? errors[firstErrorKey]
          : genderError ||
            "There are errors in the participant data. Please check again.";
        showNotification("error", firstError, "Close");
      }
    }
  };

  const handlePreviousParticipant = () => {
    if (currentParticipantIndex > 0) {
      setCurrentParticipantIndex(currentParticipantIndex - 1);
      setGenderError("");
    }
  };

  const openModal = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setIsModalOpen(true);
  };

  const closeModal = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setIsModalOpen(false);
  };

  const handleCertificateUpload = async (e, participantIndex = null) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      showNotification("error", "File size too large (max 5MB).", "Close");
      return;
    }

    setUploadingCertificate(true);
    try {
      const auth = getAuth();
      const userId = auth.currentUser?.uid;
      const timestamp = Date.now();
      const fileName = `cert_${userId}_${timestamp}_${file.name}`;
      const storageRef = ref(storage, `qualifications/${userId}/${fileName}`);
      
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);

      handleChange({
        target: {
          name: "certificateUrl",
          value: url
        }
      }, participantIndex);
      
      showNotification("success", "Certificate uploaded successfully!", "Close");
    } catch (err) {
      console.error("Upload error:", err);
      showNotification("error", "Failed to upload certificate. Please try again.", "Close");
    } finally {
      setUploadingCertificate(false);
    }
  };

  const isNextDisabled = () => {
    if (
      !formData.participants ||
      !formData.participants[currentParticipantIndex]
    ) {
      return true;
    }
    const currentParticipant = formData.participants[currentParticipantIndex];
    const { isValid } = validateParticipant(
      currentParticipant,
      currentParticipantIndex
    );
    return !isValid;
  };

  const getError = (field, participantIndex = null) => {
    const localError =
      participantIndex !== null
        ? errors[`${field}_${participantIndex}`]
        : errors[field];
    const formError =
      participantIndex !== null
        ? formData.errors?.[`${field}_${participantIndex}`]
        : formData.errors?.[field];
    return touched[field] ||
      (participantIndex !== null && touched[`${field}_${participantIndex}`])
      ? localError || formError
      : formError;
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-4">
      {errors.countries && (
        <p className="text-red-500 col-span-full">{errors.countries}</p>
      )}

      {isGroupRegistration &&
      formData.participants &&
      formData.participants.length === 3 ? (
        <>
          <InputField
            label="Team Name"
            name="teamName"
            required
            value={formData.teamName || ""}
            onChange={(e) => handleFieldChange(e)}
            disabled={isFormDisabled}
            error={getError("teamName")}
            labelColor={labelColor}
            placeholder="Enter team name"
          />
          <div className="col-span-full">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">
              Team Participant Data (Required 3 People, Min 1 Female)
            </h3>
            <div className="flex justify-center mb-4">
              {[1, 2, 3].map((step, index) => (
                <div key={index} className="flex items-center">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      index === currentParticipantIndex
                        ? "bg-lime-600 text-white"
                        : "bg-slate-200 text-slate-800"
                    }`}
                  >
                    {step}
                  </div>
                  {index < 2 && (
                    <div className="w-8 h-1 bg-slate-200 mx-2"></div>
                  )}
                </div>
              ))}
            </div>
            <p className="text-sm text-slate-600 mb-4">
              {currentParticipantIndex === 0
                ? "Fill in your details"
                : currentParticipantIndex === 1
                ? "Fill in first member's details"
                : "Fill in second member's details"}
            </p>
            {genderError && <p className="text-red-500 mb-4">{genderError}</p>}
            <motion.div
              key={currentParticipantIndex}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.3 }}
              className=""
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InputField
                  label="First Name"
                  name="namaDepan"
                  required
                  value={
                    formData.participants[currentParticipantIndex]?.namaDepan ||
                    ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={isFormDisabled}
                  error={getError("namaDepan", currentParticipantIndex)}
                  labelColor={labelColor}
                />
                <InputField
                  label="Last Name"
                  name="namaBelakang"
                  required
                  value={
                    formData.participants[currentParticipantIndex]
                      ?.namaBelakang || ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={isFormDisabled}
                  error={getError("namaBelakang", currentParticipantIndex)}
                  labelColor={labelColor}
                />
                <InputField
                  label="BIB Name"
                  name="bibName"
                  required
                  value={
                    formData.participants[currentParticipantIndex]?.bibName ||
                    ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={isFormDisabled}
                  error={getError("bibName", currentParticipantIndex)}
                  labelColor={labelColor}
                />
                <InputField
                  label="ID Number"
                  name="nomorIdentitas"
                  required
                  value={
                    formData.participants[currentParticipantIndex]
                      ?.nomorIdentitas || ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={isFormDisabled}
                  error={getError("nomorIdentitas", currentParticipantIndex)}
                  labelColor={labelColor}
                  placeholder={
                    formData.participants[currentParticipantIndex]?.negara ===
                    "Indonesia"
                      ? "Enter 16 digit KTP"
                      : "Enter Passport/ID number"
                  }
                />
                <InputField
                  label="Date of Birth"
                  name="tanggalLahir"
                  type="date"
                  required
                  value={
                    formData.participants[currentParticipantIndex]
                      ?.tanggalLahir || ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={isFormDisabled}
                  error={getError("tanggalLahir", currentParticipantIndex)}
                  labelColor={labelColor}
                />
                <InputField
                  label="Blood Type"
                  name="golonganDarah"
                  type="select"
                  required
                  value={
                    formData.participants[currentParticipantIndex]
                      ?.golonganDarah || ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={isFormDisabled}
                  options={[
                    { value: "", label: "Select Blood Type" },
                    { value: "A", label: "A" },
                    { value: "B", label: "B" },
                    { value: "AB", label: "AB" },
                    { value: "O", label: "O" },
                  ]}
                  error={getError("golonganDarah", currentParticipantIndex)}
                  labelColor={labelColor}
                />
                <div className="relative">
                  <InputField
                    label="Jersey Size"
                    name="sizeJersey"
                    type="select"
                    required
                    value={
                      formData.participants[currentParticipantIndex]
                        ?.sizeJersey || ""
                    }
                    onChange={(e) =>
                      handleFieldChange(e, currentParticipantIndex)
                    }
                    disabled={isFormDisabled}
                    options={[
                      { value: "", label: "Select Jersey Size" },
                      { value: "XS", label: "XS" },
                      { value: "S", label: "S" },
                      { value: "M", label: "M" },
                      { value: "L", label: "L" },
                      { value: "XL", label: "XL" },
                      { value: "XXL", label: "XXL" },
                    ]}
                    error={getError("sizeJersey", currentParticipantIndex)}
                    labelColor={labelColor}
                  />
                  <button
                    type="button"
                    onClick={openModal}
                    className="absolute top-0 right-0 mt-2 mr-2 text-lime-600 hover:text-lime-800 text-sm underline"
                    disabled={isFormDisabled}
                  >
                    View Size Chart
                  </button>
                </div>
                <InputField
                  label="Gender"
                  name="gender"
                  type="select"
                  required
                  value={
                    formData.participants[currentParticipantIndex]?.gender || ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={isFormDisabled}
                  options={[
                    { value: "", label: "Select Gender" },
                    { value: "Male", label: "Male" },
                    { value: "Female", label: "Female" },
                  ]}
                  error={getError("gender", currentParticipantIndex)}
                  labelColor={labelColor}
                />
                <InputField
                  label="Phone/WhatsApp Number"
                  name="telepon"
                  required
                  value={
                    formData.participants[currentParticipantIndex]?.telepon ||
                    ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={isFormDisabled}
                  error={getError("telepon", currentParticipantIndex)}
                  labelColor={labelColor}
                />
                <InputField
                  label="Country"
                  name="negara"
                  type="select"
                  required
                  value={
                    formData.participants[currentParticipantIndex]?.negara ||
                    "Indonesia"
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={isFormDisabled || loading.countries}
                  options={[{ value: "", label: "Select Country" }, ...countries]}
                  error={getError("negara", currentParticipantIndex)}
                  aria-busy={loading.countries}
                  labelColor={labelColor}
                />
                <InputField
                  label="Province"
                  name="provinsi"
                  type="select"
                  required={participantProvinces[currentParticipantIndex]?.length > 0}
                  value={
                    formData.participants[currentParticipantIndex]?.provinsi ||
                    ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={
                    isFormDisabled ||
                    !formData.participants[currentParticipantIndex]?.negara ||
                    loading.provinces
                  }
                  options={[
                    { value: "", label: participantProvinces[currentParticipantIndex]?.length > 0 ? "Select Province" : "No provinces available" },
                    ...participantProvinces[currentParticipantIndex],
                  ]}
                  error={getError("provinsi", currentParticipantIndex)}
                  aria-busy={loading.provinces}
                  labelColor={labelColor}
                />
                <InputField
                  label="City"
                  name="kota"
                  type="select"
                  required={participantCities[currentParticipantIndex]?.length > 0}
                  value={
                    formData.participants[currentParticipantIndex]?.kota || ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={
                    isFormDisabled ||
                    !formData.participants[currentParticipantIndex]?.provinsi ||
                    loading.cities
                  }
                  options={[
                    { value: "", label: participantCities[currentParticipantIndex]?.length > 0 ? "Select City" : "No cities available" },
                    ...participantCities[currentParticipantIndex],
                  ]}
                  error={getError("kota", currentParticipantIndex)}
                  aria-busy={loading.cities}
                  labelColor={labelColor}
                />
                <InputField
                  label="Address"
                  name="alamat"
                  required
                  value={
                    formData.participants[currentParticipantIndex]?.alamat || ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={isFormDisabled}
                  error={getError("alamat", currentParticipantIndex)}
                  labelColor={labelColor}
                />
                <InputField
                  label="Emergency Contact Number"
                  name="kontakDarurat"
                  required
                  value={
                    formData.participants[currentParticipantIndex]
                      ?.kontakDarurat || ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={isFormDisabled}
                  error={getError("kontakDarurat", currentParticipantIndex)}
                  labelColor={labelColor}
                />
                <InputField
                  label="Emergency Contact Name"
                  name="namaKontakDarurat"
                  required
                  value={
                    formData.participants[currentParticipantIndex]
                      ?.namaKontakDarurat || ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={isFormDisabled}
                  error={getError("namaKontakDarurat", currentParticipantIndex)}
                  labelColor={labelColor}
                />
                <InputField
                  label="Relationship with Emergency Contact"
                  name="hubunganKontakDarurat"
                  required
                  value={
                    formData.participants[currentParticipantIndex]
                      ?.hubunganKontakDarurat || ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={isFormDisabled}
                  error={getError(
                    "hubunganKontakDarurat",
                    currentParticipantIndex
                  )}
                  labelColor={labelColor}
                />
                <InputField
                  label="ITRA Performance Index"
                  name="performanceIndex"
                  required
                  value={
                    formData.participants[currentParticipantIndex]
                      ?.performanceIndex || ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={isFormDisabled}
                  error={getError("performanceIndex", currentParticipantIndex)}
                  labelColor={labelColor}
                  placeholder="Example: 460"
                />
                <InputField
                  label="STRAVA Activity or Race Result"
                  name="raceResultLink"
                  value={
                    formData.participants[currentParticipantIndex]
                      ?.raceResultLink || ""
                  }
                  onChange={(e) =>
                    handleFieldChange(e, currentParticipantIndex)
                  }
                  disabled={isFormDisabled}
                  required
                  error={getError("raceResultLink", currentParticipantIndex)}
                  labelColor={labelColor}
                  placeholder="Example: https://www.strava.com/activities/12523135176"
                />
              </div>
            </motion.div>
            <div className="flex justify-between mt-4">
              <button
                type="button"
                onClick={handlePreviousParticipant}
                className={`py-2 px-4 rounded-xl ${
                  currentParticipantIndex === 0
                    ? "bg-slate-300 cursor-not-allowed"
                    : "bg-lime-600 hover:bg-lime-700 text-white"
                }`}
                disabled={currentParticipantIndex === 0 || isFormDisabled}
              >
                Previous
              </button>
              <button
                type="button"
                onClick={handleNextParticipant}
                className={`py-2 px-4 rounded-xl ${
                  isNextDisabled()
                    ? "bg-slate-300 cursor-not-allowed"
                    : "bg-lime-600 hover:bg-lime-700 text-white"
                }`}
                disabled={isNextDisabled() || isFormDisabled}
              >
                {currentParticipantIndex < 2 ? "Next" : "Done"}
              </button>
            </div>
          </div>
        </>
      ) : (
        <>
          <InputField
            label="First Name"
            name="namaDepan"
            value={formData.namaDepan || ""}
            onChange={(e) => handleFieldChange(e)}
            disabled={isFormDisabled}
            required
            error={getError("namaDepan")}
            labelColor={labelColor}
          />
          <InputField
            label="Last Name"
            name="namaBelakang"
            value={formData.namaBelakang || ""}
            onChange={(e) => handleFieldChange(e)}
            disabled={isFormDisabled}
            required
            error={getError("namaBelakang")}
            labelColor={labelColor}
          />
          <InputField
            label="BIB Name"
            name="bibName"
            value={formData.bibName || ""}
            onChange={(e) => handleFieldChange(e)}
            disabled={isFormDisabled}
            required
            error={getError("bibName")}
            labelColor={labelColor}
          />
          <InputField
            label="ID Number"
            name="nomorIdentitas"
            value={formData.nomorIdentitas || ""}
            onChange={(e) => handleFieldChange(e)}
            disabled={isFormDisabled}
            required
            error={getError("nomorIdentitas")}
            labelColor={labelColor}
            placeholder="Enter Passport/ID number"
          />
          <InputField
            label="Date of Birth"
            name="tanggalLahir"
            type="date"
            value={formData.tanggalLahir || ""}
            onChange={(e) => handleFieldChange(e)}
            disabled={isFormDisabled}
            required
            error={getError("tanggalLahir")}
            labelColor={labelColor}
          />
          <InputField
            label="Blood Type"
            name="golonganDarah"
            type="select"
            value={formData.golonganDarah || ""}
            onChange={(e) => handleFieldChange(e)}
            disabled={isFormDisabled}
            required
            options={[
              { value: "", label: "Select Blood Type" },
              { value: "A", label: "A" },
              { value: "B", label: "B" },
              { value: "AB", label: "AB" },
              { value: "O", label: "O" },
            ]}
            error={getError("golonganDarah")}
            labelColor={labelColor}
          />
          <div className="relative">
            <InputField
              label="Jersey Size"
              name="sizeJersey"
              type="select"
              value={formData.sizeJersey || ""}
              onChange={(e) => handleFieldChange(e)}
              disabled={isFormDisabled}
              required
              options={[
                { value: "", label: "Select Jersey Size" },
                { value: "XS", label: "XS" },
                { value: "S", label: "S" },
                { value: "M", label: "M" },
                { value: "L", label: "L" },
                { value: "XL", label: "XL" },
                { value: "XXL", label: "XXL" },
              ]}
              error={getError("sizeJersey")}
              labelColor={labelColor}
            />
            <button
              type="button"
              onClick={openModal}
              className="absolute top-0 right-0 mt-2 mr-2 text-lime-600 hover:text-lime-800 text-sm underline"
              disabled={isFormDisabled}
            >
              View Size Chart
            </button>
          </div>
          <InputField
            label="Gender"
            name="gender"
            type="select"
            value={formData.gender || ""}
            onChange={(e) => handleFieldChange(e)}
            disabled={isFormDisabled}
            required
            error={getError("gender")}
            labelColor={labelColor}
            options={[
              { value: "", label: "Select Gender" },
              { value: "Male", label: "Male" },
              { value: "Female", label: "Female" },
            ]}
          />
          <InputField
            label="Phone/WhatsApp Number"
            name="telepon"
            value={formData.telepon || ""}
            onChange={(e) => handleFieldChange(e)}
            disabled={isFormDisabled}
            required
            error={getError("telepon")}
            labelColor={labelColor}
          />
          <InputField
            label="Country"
            name="negara"
            type="select"
            value={formData.negara || "Indonesia"}
            onChange={(e) => handleFieldChange(e)}
            disabled={isFormDisabled || loading.countries}
            options={[{ value: "", label: "Select Country" }, ...countries]}
            required
            error={getError("negara")}
            aria-busy={loading.countries}
            labelColor={labelColor}
          />
          <InputField
            label="Province"
            name="provinsi"
            type="select"
            value={formData.provinsi || ""}
            onChange={(e) => handleFieldChange(e)}
            disabled={isFormDisabled || !formData.negara || loading.provinces}
            options={[{ value: "", label: provinces.length > 0 ? "Select Province" : "No provinces available" }, ...provinces]}
            required={provinces.length > 0}
            error={getError("provinsi")}
            aria-busy={loading.provinces}
            labelColor={labelColor}
          />
          <InputField
            label="City"
            name="kota"
            type="select"
            value={formData.kota || ""}
            onChange={(e) => handleFieldChange(e)}
            disabled={isFormDisabled || !formData.provinsi || loading.cities}
            options={[{ value: "", label: cities.length > 0 ? "Select City" : "No cities available" }, ...cities]}
            required={cities.length > 0}
            error={getError("kota")}
            aria-busy={loading.cities}
            labelColor={labelColor}
          />
          <InputField
            label="Address"
            name="alamat"
            value={formData.alamat || ""}
            onChange={(e) => handleFieldChange(e)}
            disabled={isFormDisabled}
            required
            error={getError("alamat")}
            labelColor={labelColor}
          />
          <InputField
            label="Emergency Contact Number"
            name="kontakDarurat"
            value={formData.kontakDarurat || ""}
            onChange={(e) => handleFieldChange(e)}
            disabled={isFormDisabled}
            required
            error={getError("kontakDarurat")}
            labelColor={labelColor}
          />
          <InputField
            label="Emergency Contact Name"
            name="namaKontakDarurat"
            value={formData.namaKontakDarurat || ""}
            onChange={(e) => handleFieldChange(e)}
            disabled={isFormDisabled}
            required
            error={getError("namaKontakDarurat")}
            labelColor={labelColor}
          />
          <InputField
            label="Relationship with Emergency Contact"
            name="hubunganKontakDarurat"
            value={formData.hubunganKontakDarurat || ""}
            onChange={(e) => handleFieldChange(e)}
            disabled={isFormDisabled}
            required
            error={getError("hubunganKontakDarurat")}
            labelColor={labelColor}
          />
          <InputField
            label="ITRA Performance Index"
            name="performanceIndex"
            value={formData.performanceIndex || ""}
            onChange={(e) => handleFieldChange(e)}
            disabled={isFormDisabled}
            error={getError("performanceIndex")}
            labelColor={labelColor}
            placeholder="Example: 460"
          />
          {!skipsRaceProof && (
            <InputField
              label="STRAVA Activity or Race Result"
              name="raceResultLink"
              value={formData.raceResultLink || ""}
              onChange={(e) => handleFieldChange(e)}
              disabled={isFormDisabled}
              required
              error={getError("raceResultLink")}
              labelColor={labelColor}
              placeholder="Example: https://www.strava.com/activities/12523135176"
            />
          )}

          {!skipsRaceProof && (
            <div className="space-y-1">
              <InputField
                label="Upload Certificate (Max 5MB)"
                name="certificate"
                type="file"
                accept="image/*,application/pdf"
                onChange={(e) => handleCertificateUpload(e)}
                disabled={isFormDisabled || uploadingCertificate}
                error={getError("certificateUrl")}
                labelColor={labelColor}
              />
              {formData.certificateUrl && (
                <div className="text-xs text-lime-600 pl-1">
                  File uploaded: <a href={formData.certificateUrl} target="_blank" rel="noopener noreferrer" className="underline font-medium">View Certificate</a>
                </div>
              )}
              {uploadingCertificate && <span className="text-xs text-slate-500 pl-1">Uploading...</span>}
            </div>
          )}

          <InputField
            label="Medical History (Disease / Allergy)"
            name="riwayatMedis"
            value={formData.riwayatMedis || ""}
            onChange={(e) => handleFieldChange(e)}
            disabled={isFormDisabled}
            error={getError("riwayatMedis")}
            labelColor={labelColor}
            placeholder="Example: Asthma, specific drug allergies. Fill 'None' if none."
          />
        </>
      )}

      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 flex items-center justify-center z-50"
            onClick={closeModal}
          >
            <motion.div
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.8 }}
              className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[80vh] overflow-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={sizeChartUrl}
                alt="Size Chart"
                className="w-full h-auto"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
